'use client';

import { useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  ArrowRight,
  Banknote,
  Check,
  Copy,
  DollarSign,
  FileCheck2,
  Gift,
  Hourglass,
  MousePointerClick,
  Share2,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { Badge, type BadgeVariant } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { PageHeader } from '@/components/ui/page-header';
import { Skeleton } from '@/components/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { useOrgNavigate } from '@/hooks/use-app-navigate';
import { useReferralEnabled } from '@/hooks/use-referral-enabled';
import { trpc } from '@/lib/api/trpc';

const usd = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 2 }).format(cents / 100);

const day = (value: string | Date | null | undefined) =>
  value ? new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }) : '—';

const monthLabel = (ym: string) =>
  new Date(`${ym}-01T00:00:00Z`).toLocaleDateString(undefined, { month: 'short', year: '2-digit', timeZone: 'UTC' });

/** `br_1a2b3c4d5e6f` → `…5e6f` — enough to quote to support, never the referee's invoice itself. */
const shortId = (id: string | null) => (id ? `…${id.slice(-6)}` : '—');

const SETTLEMENT_VARIANT: Record<string, BadgeVariant> = {
  requested: 'yellow',
  approved: 'blue',
  paid: 'green',
  rejected: 'red',
  cancelled: 'gray',
};

const REFEREE_VARIANT: Record<string, BadgeVariant> = {
  signed_up: 'blue',
  paying: 'green',
  inactive: 'gray',
  void: 'red',
};

function CopyField({ label, value }: { label: string; value: string }) {
  const { t } = useTranslation('referral');
  const [copied, setCopied] = useState(false);
  const copy = () => {
    void navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</p>
      <div className="flex flex-1 items-center overflow-hidden rounded-lg border bg-background">
        <input type="text" readOnly value={value} className="flex-1 truncate bg-transparent px-4 py-2.5 font-mono text-sm outline-none" />
        <Button variant={copied ? 'ghost' : 'secondary'} size="sm" className="shrink-0 rounded-none border-l" onClick={copy}>
          {copied ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Copy className="mr-1.5 h-3.5 w-3.5" />}
          {copied ? t('copied') : t('copy')}
        </Button>
      </div>
    </div>
  );
}

function FunnelStep({
  icon: Icon,
  label,
  value,
  hint,
  color,
  last,
}: {
  icon: typeof Users;
  label: string;
  value: string | number;
  hint: string;
  color: string;
  last?: boolean;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="min-w-0 flex-1 rounded-lg border p-4">
        <div className={`mb-2 flex h-8 w-8 items-center justify-center rounded-lg ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <p className="text-2xl font-bold">{value}</p>
        <p className="text-xs font-medium">{label}</p>
        <p className="mt-0.5 text-xs text-muted-foreground">{hint}</p>
      </div>
      {!last && <ArrowRight className="hidden h-4 w-4 shrink-0 text-muted-foreground md:block" />}
    </div>
  );
}

export default function ReferralPage() {
  const { t } = useTranslation('referral');
  const referralEnabled = useReferralEnabled();
  const { orgRoutes } = useOrgNavigate();
  const utils = trpc.useUtils();
  const { data: referral, isLoading, error } = trpc.referral.getReferralData.useQuery(undefined, { enabled: referralEnabled });

  const refresh = () => utils.referral.getReferralData.invalidate();

  const convert = trpc.referral.convertToCredits.useMutation({
    onSuccess: (res) => {
      toast.success(t('balance.converted', { credits: usd(res.creditCents) }));
      setConvertOpen(false);
      void refresh();
    },
    onError: (e) => toast.error(e.message),
  });
  const transfer = trpc.referral.requestBankTransfer.useMutation({
    onSuccess: () => {
      toast.success(t('balance.requested'));
      setTransferOpen(false);
      void refresh();
    },
    onError: (e) => toast.error(e.message),
  });
  const cancelTransfer = trpc.referral.cancelBankTransfer.useMutation({
    onSuccess: () => void refresh(),
    onError: (e) => toast.error(e.message),
  });
  const applyCode = trpc.referral.applyCode.useMutation({
    onSuccess: (res) => {
      toast.success(t('referredBy.success', { credits: usd(res?.welcomeCreditCents ?? 0) }));
      setReferredCode('');
      void refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  const savePayout = trpc.referral.savePayoutAccount.useMutation({
    onSuccess: () => {
      toast.success(t('payoutAccount.savedToast'));
      setBankFormOpen(false);
      void refresh();
    },
    onError: (e) => toast.error(e.message),
  });
  const removePayout = trpc.referral.deletePayoutAccount.useMutation({
    onSuccess: () => {
      toast.success(t('payoutAccount.removedToast'));
      void refresh();
    },
    onError: (e) => toast.error(e.message),
  });

  const [bankFormOpen, setBankFormOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [referredCode, setReferredCode] = useState('');
  const [bank, setBank] = useState({ beneficiaryName: '', bankName: '', iban: '', swiftCode: '', accountNumber: '', routingNumber: '', country: '' });

  const amountCents = useMemo(() => Math.round((Number.parseFloat(amount.replace(',', '.')) || 0) * 100), [amount]);

  // Referral is Tagada-branded; never expose it under a whitelabel/partner.
  // Placed after all hooks so the hook order stays stable across renders.
  if (!referralEnabled) {
    return <Navigate to={orgRoutes.ANALYTICS()} replace />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-48 w-full" />
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-destructive/10">
            <Gift className="h-6 w-6 text-destructive" />
          </div>
          <p className="text-sm text-muted-foreground">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!referral) return null;

  const { funnel, stats, referredBy } = referral;
  const available = stats.availableCents;
  const minTransfer = referral.program.minBankTransferCents;
  const canAct = referral.eligible && available > 0;
  const payoutAccount = referral.payoutAccount;
  const bankFields = [
    ['beneficiaryName', 'balance.bank.beneficiaryName'],
    ['bankName', 'balance.bank.bankName'],
    ['iban', 'balance.bank.iban'],
    ['swiftCode', 'balance.bank.swiftCode'],
    ['accountNumber', 'balance.bank.accountNumber'],
    ['routingNumber', 'balance.bank.routingNumber'],
    ['country', 'balance.bank.country'],
  ] as const;
  const bankPayload = () => ({
    beneficiaryName: bank.beneficiaryName,
    bankName: bank.bankName || undefined,
    iban: bank.iban || undefined,
    swiftCode: bank.swiftCode || undefined,
    accountNumber: bank.accountNumber || undefined,
    routingNumber: bank.routingNumber || undefined,
    address: bank.country ? { country: bank.country.toUpperCase() } : undefined,
  });
  const bankFormValid = bank.beneficiaryName.trim().length >= 2 && (bank.iban.replace(/\s+/g, '').length >= 8 || (bank.accountNumber.length >= 4 && bank.routingNumber.length >= 4));
  const hasChartData = referral.monthly.some((m) => m.commissionCents !== 0 || m.paidReports > 0 || m.clicks > 0);
  const chartData = referral.monthly.map((m) => ({ ...m, label: monthLabel(m.month), commission: m.commissionCents / 100 }));

  const steps = [
    { icon: Share2, title: t('steps.shareTitle'), desc: t('steps.shareDesc') },
    { icon: UserPlus, title: t('steps.signupTitle'), desc: t('steps.signupDesc') },
    { icon: Banknote, title: t('steps.earnTitle'), desc: t('steps.earnDesc') },
  ];

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('pageTitle')} description={t('pageDescription')} />

      {referredBy && (
        <Card className="border-emerald-500/30 bg-emerald-500/5">
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/20">
                <Gift className="h-5 w-5 text-emerald-600" />
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold">{t('referredBanner.title', { code: referredBy.code })}</p>
                <p className="text-xs text-muted-foreground">
                  {referredBy.welcomeCreditCents > 0 && referredBy.creditBalanceCents > 0
                    ? t('referredBanner.credits', { welcome: usd(referredBy.welcomeCreditCents), remaining: usd(referredBy.creditBalanceCents) })
                    : t('referredBanner.creditsUsed', { welcome: usd(referredBy.welcomeCreditCents) })}
                </p>
              </div>
            </div>
            <Button variant="outline" size="sm" asChild>
              <Link to={`${orgRoutes.BILLING()}?tab=credits`}>{t('referredBanner.cta')}</Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {!referral.eligible && (
        <Card className="border-amber-500/30 bg-amber-500/5">
          <CardContent className="p-5">
            <p className="text-sm font-semibold">{t('ineligibleTitle')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('ineligibleBody')}</p>
          </CardContent>
        </Card>
      )}

      <Card className="border-emerald-500/20 bg-gradient-to-br from-emerald-500/5 via-teal-500/5 to-cyan-500/5">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-500/20">
              <Gift className="h-5 w-5 text-emerald-500" />
            </div>
            <div>
              <CardTitle className="text-lg">{t('shareAndEarn')}</CardTitle>
              <CardDescription>{t('commissionRate')}</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-5">
          <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
            {t('descriptionPrefix')}
            <strong className="text-emerald-600 dark:text-emerald-400">{t('descriptionHighlight')}</strong>
            {t('descriptionSuffix')}
          </p>
          <div className="grid max-w-3xl gap-3 md:grid-cols-2">
            <CopyField label={t('yourCode')} value={referral.code} />
            <CopyField label={t('directSignupLink')} value={referral.referralLink} />
            <CopyField label={t('landingPageLink')} value={referral.landingLink} />
            <CopyField label={t('tagadapayLink')} value={referral.tagadapayLink} />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('funnel.title')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-3 md:grid-cols-4">
          <FunnelStep icon={MousePointerClick} color="bg-blue-500/10 text-blue-500" label={t('funnel.clicks')} value={funnel.clicksTotal} hint={t('funnel.clicks30d', { count: funnel.clicks30d })} />
          <FunnelStep icon={Users} color="bg-purple-500/10 text-purple-500" label={t('funnel.signups')} value={funnel.signups} hint={funnel.clickToSignupPct === null ? '—' : t('funnel.signupsRate', { pct: funnel.clickToSignupPct })} />
          <FunnelStep icon={TrendingUp} color="bg-amber-500/10 text-amber-500" label={t('funnel.paying')} value={funnel.paying} hint={t('funnel.everPaid', { count: funnel.everPaid })} />
          <FunnelStep icon={Hourglass} color="bg-emerald-500/10 text-emerald-500" label={t('funnel.pending')} value={usd(stats.pendingCommissionCents)} hint={t('funnel.pendingHelp', { count: referral.referees.reduce((n, r) => n + r.openReports, 0) })} last />
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        {[
          { label: t('stats.paidReports'), value: stats.paidReports, icon: FileCheck2, color: 'text-blue-500', bg: 'bg-blue-500/10' },
          { label: t('stats.earned'), value: usd(stats.earnedCents), icon: DollarSign, color: 'text-emerald-500', bg: 'bg-emerald-500/10' },
          { label: t('balance.settled'), value: usd(stats.settledCents), icon: Banknote, color: 'text-slate-500', bg: 'bg-slate-500/10' },
          { label: t('stats.available'), value: usd(available), icon: Wallet, color: 'text-emerald-600', bg: 'bg-emerald-500/10', highlight: true },
        ].map((s) => (
          <Card key={s.label} className={s.highlight ? 'border-emerald-500/20' : ''}>
            <CardContent className="p-5">
              <div className={`mb-3 flex h-9 w-9 items-center justify-center rounded-lg ${s.bg}`}>
                <s.icon className={`h-4 w-4 ${s.color}`} />
              </div>
              <p className={`text-2xl font-bold ${s.highlight ? 'text-emerald-600 dark:text-emerald-400' : ''}`}>{s.value}</p>
              <p className="mt-1 text-xs text-muted-foreground">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('chart.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {hasChartData ? (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} vertical={false} />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v: number) => `$${v}`} width={48} />
                <Tooltip
                  cursor={{ fill: 'rgba(16,185,129,0.08)' }}
                  content={({ payload }) => {
                    const d = payload?.[0]?.payload as (typeof chartData)[number] | undefined;
                    if (!d) return null;
                    return (
                      <div className="rounded-lg border bg-background p-2 text-xs shadow-md">
                        <p className="font-medium">{d.label}</p>
                        <p>{t('chart.commission')}: {usd(d.commissionCents)}</p>
                        <p>{t('chart.paidReports')}: {d.paidReports}</p>
                        <p>{t('chart.signups')}: {d.signups} · {t('chart.clicks')}: {d.clicks}</p>
                      </div>
                    );
                  }}
                />
                <Bar dataKey="commission" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <p className="text-sm text-muted-foreground">{t('chart.empty')}</p>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('balance.title')}</CardTitle>
          <CardDescription>
            {t('balance.available')}: <strong>{usd(available)}</strong> · {t('balance.pending')}: {usd(stats.pendingCents)} · {t('balance.settled')}: {usd(stats.settledCents)}
          </CardDescription>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div className="rounded-lg border p-4">
            <p className="text-sm font-semibold">{t('balance.convertTitle')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('balance.convertHelp')}</p>
            <Button className="mt-3" size="sm" disabled={!canAct} onClick={() => { setAmount((available / 100).toFixed(2)); setConvertOpen(true); }}>
              <Gift className="mr-1.5 h-3.5 w-3.5" />
              {t('balance.convertButton')}
            </Button>
          </div>
          <div className="rounded-lg border p-4">
            <p className="text-sm font-semibold">{t('balance.transferTitle')}</p>
            <p className="mt-1 text-xs text-muted-foreground">{t('balance.transferHelp', { min: usd(minTransfer) })}</p>
            {!payoutAccount && <p className="mt-1 text-xs text-amber-600">{t('payoutAccount.transferNeeds')}</p>}
            <Button className="mt-3" size="sm" variant="outline" disabled={!canAct || available < minTransfer || !payoutAccount} onClick={() => { setAmount((available / 100).toFixed(2)); setTransferOpen(true); }}>
              <Banknote className="mr-1.5 h-3.5 w-3.5" />
              {t('balance.transferButton')}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-start justify-between gap-4">
          <div>
            <CardTitle className="text-base">{t('payoutAccount.title')}</CardTitle>
            <CardDescription>{t('payoutAccount.help')}</CardDescription>
          </div>
          {!bankFormOpen && (
            <div className="flex shrink-0 gap-2">
              <Button size="sm" variant={payoutAccount ? 'outline' : 'default'} disabled={!referral.eligible} onClick={() => setBankFormOpen(true)}>
                {payoutAccount ? t('payoutAccount.replace') : t('payoutAccount.add')}
              </Button>
              {payoutAccount && (
                <Button size="sm" variant="ghost" disabled={removePayout.isPending} onClick={() => removePayout.mutate()}>
                  {t('payoutAccount.remove')}
                </Button>
              )}
            </div>
          )}
        </CardHeader>
        <CardContent>
          {!bankFormOpen ? (
            <p className="text-sm">
              {payoutAccount
                ? payoutAccount.bankName
                  ? t('payoutAccount.saved', { name: payoutAccount.beneficiaryName, bank: payoutAccount.bankName, last4: payoutAccount.last4 ?? '' })
                  : t('payoutAccount.savedNoBank', { name: payoutAccount.beneficiaryName, last4: payoutAccount.last4 ?? '' })
                : <span className="text-muted-foreground">{t('payoutAccount.none')}</span>}
            </p>
          ) : (
            <div className="space-y-3">
              <p className="text-xs text-muted-foreground">{t('balance.bank.help')}</p>
              <div className="grid gap-3 md:grid-cols-2">
                {bankFields.map(([key, label]) => (
                  <div key={key} className="space-y-1">
                    <Label htmlFor={`payout-${key}`}>{t(label)}</Label>
                    <Input id={`payout-${key}`} value={bank[key]} onChange={(e) => setBank({ ...bank, [key]: e.target.value })} />
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" disabled={!bankFormValid || savePayout.isPending} onClick={() => savePayout.mutate(bankPayload())}>
                  {t('payoutAccount.save')}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setBankFormOpen(false)}>{t('payoutAccount.cancel')}</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {!referredBy && referral.eligible && (
        <Card className="bg-muted/30">
          <CardContent className="flex flex-col gap-3 p-5 sm:flex-row sm:items-end">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold">{t('referredBy.title')}</p>
              <p className="text-xs text-muted-foreground">{t('referredBy.help')}</p>
            </div>
            <div className="flex gap-2">
              <Input value={referredCode} onChange={(e) => setReferredCode(e.target.value)} placeholder={t('referredBy.placeholder')} className="w-40 font-mono uppercase" />
              <Button size="sm" disabled={referredCode.trim().length < 6 || applyCode.isPending} onClick={() => applyCode.mutate({ code: referredCode })}>
                {t('referredBy.button')}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('referees.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {referral.referees.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('referees.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('referees.merchant')}</TableHead>
                  <TableHead>{t('referees.status')}</TableHead>
                  <TableHead>{t('referees.since')}</TableHead>
                  <TableHead className="text-right">{t('referees.paidReports')}</TableHead>
                  <TableHead>{t('referees.lastPaid')}</TableHead>
                  <TableHead className="text-right">{t('referees.pending')}</TableHead>
                  <TableHead className="text-right">{t('referees.base')}</TableHead>
                  <TableHead className="text-right">{t('referees.commission')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referral.referees.map((r) => (
                  <TableRow key={r.accountId}>
                    <TableCell className="font-medium">{r.name ?? r.accountId}</TableCell>
                    <TableCell>
                      <Badge variant={REFEREE_VARIANT[r.status] ?? 'gray'} title={t(`referees.statusHelp.${r.status}`)}>
                        {t(`referees.statusLabel.${r.status}`)}
                      </Badge>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">{day(r.attributedAt)}</TableCell>
                    <TableCell className="text-right">{r.paidReports}</TableCell>
                    <TableCell className="whitespace-nowrap">{day(r.lastPaidAt)}</TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      {r.pendingCommissionCents > 0 ? `${usd(r.pendingCommissionCents)} (${r.openReports})` : '—'}
                    </TableCell>
                    <TableCell className="text-right">{usd(r.baseFiatCents)}</TableCell>
                    <TableCell className="text-right font-semibold text-emerald-600">{usd(r.commissionCents)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('ledger.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {referral.ledger.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('ledger.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('ledger.date')}</TableHead>
                  <TableHead>{t('ledger.detail')}</TableHead>
                  <TableHead>{t('ledger.invoice')}</TableHead>
                  <TableHead className="text-right">{t('ledger.base')}</TableHead>
                  <TableHead className="text-right">{t('ledger.rate')}</TableHead>
                  <TableHead className="text-right">{t('ledger.amount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referral.ledger.map((row) => (
                  <TableRow key={row.id}>
                    <TableCell className="whitespace-nowrap">{day(row.postedAt)}</TableCell>
                    <TableCell>
                      <span className="font-medium">{t(`ledger.kind.${row.kind}`)}</span>
                      {row.refereeName && <span className="text-muted-foreground"> · {row.refereeName}</span>}
                      {row.reportPeriodStart && <span className="text-muted-foreground"> · {t('ledger.weekOf', { date: day(row.reportPeriodStart) })}</span>}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground" title={row.billingReportId ?? undefined}>
                      {shortId(row.billingReportId)}
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">{row.billingReportId ? usd(Math.abs(row.baseFiatCents)) : '—'}</TableCell>
                    <TableCell className="text-right text-muted-foreground">{row.billingReportId ? `${row.rateBps / 100}%` : '—'}</TableCell>
                    <TableCell className={`text-right font-semibold ${row.amountCents < 0 ? 'text-destructive' : 'text-emerald-600'}`}>
                      {row.amountCents < 0 ? '−' : '+'}
                      {usd(Math.abs(row.amountCents))}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('settlements.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          {referral.settlements.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('settlements.empty')}</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('settlements.date')}</TableHead>
                  <TableHead>{t('settlements.kind')}</TableHead>
                  <TableHead>{t('settlements.status')}</TableHead>
                  <TableHead className="text-right">{t('settlements.amount')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {referral.settlements.map((s) => (
                  <TableRow key={s.id}>
                    <TableCell className="whitespace-nowrap">{day(s.createdAt)}</TableCell>
                    <TableCell>
                      {s.source === 'firstpromoter' ? t('settlements.kindLabel.firstpromoter') : t(`settlements.kindLabel.${s.kind}`)}
                    </TableCell>
                    <TableCell>
                      <Badge variant={SETTLEMENT_VARIANT[s.status] ?? 'gray'}>{t(`settlements.statusLabel.${s.status}`)}</Badge>
                      {s.kind === 'bank_transfer' && s.status === 'requested' && (
                        <Button variant="link" size="sm" className="h-auto px-2 py-0 text-xs" onClick={() => cancelTransfer.mutate({ settlementId: s.id })}>
                          {t('balance.cancel')}
                        </Button>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {usd(s.amountCents)}
                      {s.kind === 'credits' && s.creditCents ? <span className="text-xs text-muted-foreground"> → {usd(s.creditCents)}</span> : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t('howItWorks')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6 md:grid-cols-3">
            {steps.map((step, i) => (
              <div key={i} className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10">
                  <step.icon className="h-5 w-5 text-emerald-500" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{step.title}</p>
                  <p className="mt-1 text-xs leading-relaxed text-muted-foreground">{step.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Dialog open={convertOpen} onOpenChange={setConvertOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('balance.convertTitle')}</DialogTitle>
            <DialogDescription>{t('balance.convertHelp')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="convert-amount">{t('balance.amountLabel')}</Label>
            <div className="flex gap-2">
              <Input id="convert-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Button variant="outline" onClick={() => setAmount((available / 100).toFixed(2))}>{t('balance.amountAll')}</Button>
            </div>
            <p className="text-xs text-muted-foreground">
              {t('balance.convertPreview', { credits: usd(Math.round(amountCents * referral.program.creditMultiplier)), amount: usd(amountCents) })}
            </p>
          </div>
          <DialogFooter>
            <Button disabled={amountCents <= 0 || amountCents > available || convert.isPending} onClick={() => convert.mutate({ amountCents })}>
              {t('balance.convertButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={transferOpen} onOpenChange={setTransferOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('balance.transferTitle')}</DialogTitle>
            <DialogDescription>
              {payoutAccount ? t('payoutAccount.transferUses', { name: payoutAccount.beneficiaryName, last4: payoutAccount.last4 ?? '' }) : t('payoutAccount.transferNeeds')}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            <Label htmlFor="transfer-amount">{t('balance.amountLabel')}</Label>
            <div className="flex gap-2">
              <Input id="transfer-amount" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} />
              <Button variant="outline" onClick={() => setAmount((available / 100).toFixed(2))}>{t('balance.amountAll')}</Button>
            </div>
            <p className="text-xs text-muted-foreground">{t('balance.transferHelp', { min: usd(minTransfer) })}</p>
          </div>
          <DialogFooter>
            <Button disabled={!payoutAccount || amountCents < minTransfer || amountCents > available || transfer.isPending} onClick={() => transfer.mutate({ amountCents })}>
              {t('balance.transferButton')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
